import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, Search, AlertTriangle } from "lucide-react";

/**
 * Searchable dropdown for picking a project on the Hours tab.
 *
 * Props:
 *  - value          currently-selected project_id ("" = none)
 *  - onChange(id)   called with the picked project_id
 *  - onCreate()     optional; called when the user picks "+ Create new job…"
 *  - pinned         array of {project_id, name} pinned to the top (Office/Estimating)
 *  - projects       array of {project_id, name, needs_review}
 *  - placeholder    text shown when no value is set
 *  - className      extra classes applied to the trigger button
 *  - testid         data-testid for the trigger button
 *  - allowClear     if true, adds a "— none —" option at the top of the list
 *  - createLabel    label for the create action (default "Create new job…")
 *  - emptyLabel     text when the filter returns nothing (default "No matching jobs.")
 */
export default function JobPicker({
  value,
  onChange,
  onCreate,
  pinned = [],
  projects = [],
  placeholder = "— job —",
  className = "",
  testid,
  allowClear = true,
  createLabel = "Create new job…",
  emptyLabel = "No matching jobs.",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Flip-up when the trigger is near the bottom of the window: if the
  // popover would extend past the viewport bottom, open upward instead.
  const [dropUp, setDropUp] = useState(false);
  const [popMaxH, setPopMaxH] = useState(320);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const POPOVER_MAX = 320; // search bar + list + create row, generous
  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next && rootRef.current) {
        const rect = rootRef.current.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom;
        const above = rect.top;
        const up = below < POPOVER_MAX && above > below;
        setDropUp(up);
        setPopMaxH(Math.max(160, Math.min(POPOVER_MAX, (up ? above : below) - 8)));
      }
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    // Focus the search input on open.
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selectedName = useMemo(() => {
    if (!value) return "";
    const hit = pinned.find((p) => p.project_id === value) || projects.find((p) => p.project_id === value);
    return hit?.name || value;
  }, [value, pinned, projects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [projects, query]);

  const filteredPinned = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pinned;
    return pinned.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [pinned, query]);

  const pick = (id) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  const invalidNeedsReview = value
    ? !!projects.find((p) => p.project_id === value)?.needs_review
    : false;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-1 rounded border px-2 text-left ${
          invalidNeedsReview
            ? "border-amber-500/60 bg-amber-500/10 text-amber-200 dark:text-amber-300"
            : "border-white/10 bg-white/[0.03] text-white/80 hover:border-white/20"
        } ${className}`}
      >
        <span className="truncate">
          {invalidNeedsReview && <AlertTriangle className="mr-1 inline h-3 w-3" />}
          {selectedName || placeholder}
        </span>
        <ChevronDown className={`h-3 w-3 shrink-0 opacity-60 transition-transform ${open && dropUp ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`absolute left-0 right-0 z-50 flex flex-col overflow-hidden rounded-md border border-white/15 bg-[#0b0b0f] text-sm text-white shadow-xl dark:bg-[#0b0b0f] ${
            dropUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
          style={{ maxHeight: popMaxH }}
          role="listbox"
          data-testid={testid ? `${testid}-popover` : undefined}
        >
          <div className="shrink-0 border-b border-white/10 p-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(ev) => setQuery(ev.target.value)}
                placeholder="Search jobs…"
                data-testid={testid ? `${testid}-search` : undefined}
                className="h-8 w-full rounded border border-white/10 bg-white/[0.05] pl-7 pr-2 text-xs text-white placeholder:text-white/40 focus:border-[#1a3fd1] focus:outline-none"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto py-1">
            {allowClear && !query && (
              <Row picked={!value} onClick={() => pick("")}>
                <span className="text-white/50">— none —</span>
              </Row>
            )}
            {filteredPinned.map((p) => (
              <Row key={p.project_id} picked={value === p.project_id} onClick={() => pick(p.project_id)}>
                {p.name}
              </Row>
            ))}
            {filteredPinned.length > 0 && (filtered.length > 0) && (
              <div className="my-1 border-t border-white/5" />
            )}
            {filtered.map((p) => (
              <Row key={p.project_id} picked={value === p.project_id} onClick={() => pick(p.project_id)}>
                {p.needs_review ? <AlertTriangle className="mr-1 inline h-3 w-3 text-amber-400" /> : null}
                <span className={p.needs_review ? "text-amber-300" : ""}>{p.name}</span>
              </Row>
            ))}
            {filtered.length === 0 && filteredPinned.length === 0 && (
              <p className="px-3 py-2 text-xs text-white/40">{emptyLabel}</p>
            )}
          </div>
          {onCreate && (
            <button
              type="button"
              onClick={() => { setOpen(false); setQuery(""); onCreate(); }}
              data-testid={testid ? `${testid}-create` : undefined}
              className="flex w-full shrink-0 items-center gap-1.5 border-t border-white/10 px-3 py-2 text-left text-xs font-semibold text-[#8fa4f0] hover:bg-white/[0.05]"
            >
              <Plus className="h-3.5 w-3.5" /> {createLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const Row = ({ picked, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    role="option"
    aria-selected={picked}
    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs ${
      picked ? "bg-white/[0.08] text-white" : "text-white/80 hover:bg-white/[0.05]"
    }`}
  >
    <span className="truncate">{children}</span>
    {picked && <Check className="h-3 w-3 shrink-0 text-emerald-400" />}
  </button>
);
